import {Type} from '@angular/core';
import {concat, EMPTY, forkJoin, merge, Observable, of} from 'rxjs';
import {catchError, defaultIfEmpty, map, scan, take} from 'rxjs/operators';
import {Filter} from '../../../api/filter-interface';
import {Resource} from '../../../api/resource';
import {InternalPropertyMap} from '../../../basic-entity-back/basic-entity-interface/mapping-internal';
import {TypeStr} from '../../../basic-entity-back/property-type/type-str';
import {
    EndSearchFilter, ExactSearchFilter, MultipleSearchFilter, PartialSearchFilter, SimpleSearchFilter, StartSearchFilter,
    WordStartSearchFilter
} from '../../../basic-entity-back/filters/search-filter';
import {BooleanFilter} from '../../../basic-entity-back/filters/boolean-filter';
import {ExistsFilter} from '../../../basic-entity-back/filters/exists-filter';
import {NumericFilter} from '../../../basic-entity-back/filters/numeric-filter';
import {DateFilters} from '../../../basic-entity-back/filters/date-filter';
import {GeneralSearchFilter} from '../../../basic-entity-back/filters/general-search-filter';
import {IriSearchFilter} from '../../../basic-entity-back/filters/iri-search-filter';
import {MAX_SUGERENCIAS, OpcionesFiltroService} from './opciones-filtro.service';
import {OpcionFiltro, SeccionFiltro, TipoSeccion} from './panel-filtros';

/** Ajustes de una tabla sobre el panel que se genera a partir de su modelo (`[ajustesPanel]` en be-table) */
export interface AjustesPanel {
    /**
     * Por modelKey: cambiar título, tipo, porDefecto, destacadas, limpiar, opciones… o `oculta: true`.
     * Un `porDefecto` nunca se aplica en una tabla cuya URL ya trae algún filtro (p. ej. uno fijo del menú): los
     * valores por defecto solo se aplican con la URL sin filtros.
     */
    secciones?: { [propiedad: string]: Partial<SeccionFiltro> & { oculta?: boolean } };
    /** Orden explícito (modelKeys); lo no listado va detrás en el orden por defecto */
    orden?: string[];
    /** Secciones extra (p. ej. propiedades de solo filtro) */
    extra?: SeccionFiltro[];
}

/** El tipo de sección y el filtro que usará el panel para una propiedad */
export interface EleccionFiltro {
    tipo: TipoSeccion;
    filtro: Filter;
}

/** Filtros que nunca son sección: los cubre la búsqueda de la cabecera, o no filtran por la propiedad */
const FILTROS_SIN_SECCION: Filter[] = [GeneralSearchFilter, IriSearchFilter];

const TIPOS_TEXTO: string[] = [TypeStr.String, TypeStr.Text, TypeStr.Email, TypeStr.PhoneNumber];
/** Los números con NumberCaster: el texto del panel llega a NumericFilter convertido a número */
const TIPOS_NUMERO_CON_CASTER: string[] = [TypeStr.Integer, TypeStr.Float];
/** Decimal y Financial no tienen NumberCaster (NumericFilter lanzaría con el texto): solo el exacto */
const TIPOS_NUMERO_SIN_CASTER: string[] = [TypeStr.Decimal, TypeStr.Financial];
const TIPOS_FECHA: string[] = [TypeStr.Date, TypeStr.DateTime];
const TIPOS_RELACION: string[] = [TypeStr.NestedModel, TypeStr.Uri];

/** Preferencias: de los filtros declarados en la propiedad, el primero de estas listas que esté */
const PREFERENCIA_TEXTO: Filter[] = [
    PartialSearchFilter, StartSearchFilter, WordStartSearchFilter, EndSearchFilter, ExactSearchFilter,
    SimpleSearchFilter, MultipleSearchFilter
];
const PREFERENCIA_NUMERO: Filter[] = [NumericFilter, ExactSearchFilter];
/**
 * Las casillas mandan varios valores (`x[]`): solo los filtros que aceptan arrays. Si no se declara ninguno de estos,
 * vale también uno que herede del exacto (p. ej. EstadoPedidoClienteFilter en Pedidos del cliente, que traduce cada
 * opción a sus estados reales), y la sección usa ese mismo: es el que lleva la URL (`exsep`) y por el que el panel
 * reconoce lo suyo al volver de la edición.
 */
const PREFERENCIA_LISTA: Filter[] = [ExactSearchFilter, MultipleSearchFilter];

/** Un filtro hijo del exacto (no el exacto mismo): acepta arrays igual que él */
const esHijoDelExacto = (filtro: Filter): boolean => filtro.prototype instanceof ExactSearchFilter;

/** El tipo de una propiedad, o null si no se puede leer (un FutureUri que nadie sustituyó lanza en toString) */
function tipoDe(propiedad: InternalPropertyMap): string | null {
    try {
        return propiedad.type.toString();
    } catch {
        return null;
    }
}

/**
 * Qué sección le toca a una propiedad y con qué filtro (tabla del §1 del spec), o null si ninguna:
 * - Choice → casillas; NestedModel / Uri → casillas (el generador las pasa a buscador si el modelo es enorme):
 *   exacto o múltiple.
 * - Boolean + BooleanFilter → Sí / No.
 * - String / Text (y Email / Phone) → texto: partial > start > word-start > end > exact > simple > multiple.
 * - Integer / Float → texto numérico: numérico > exacto; Decimal / Financial: solo exacto.
 * - Date / DateTime → fechas con cualquier filtro de fecha (el primero declarado; el panel manda after / before).
 * - Si nada de lo anterior encaja y la propiedad tiene ExistsFilter → Sí / No sobre «existe».
 * GeneralSearchFilter e IriSearchFilter se ignoran. Pura.
 */
export function filtroDeSeccion(propiedad: InternalPropertyMap): EleccionFiltro | null {
    const filtros = (propiedad.filters || []).filter(f => !FILTROS_SIN_SECCION.includes(f));
    if (filtros.length === 0) {
        return null;
    }
    const tipo = tipoDe(propiedad);
    const preferido = (preferencia: Filter[]) => preferencia.find(f => filtros.includes(f));
    const eleccion = (tipoSeccion: TipoSeccion, filtro: Filter | undefined): EleccionFiltro | null =>
        filtro ? {tipo: tipoSeccion, filtro} : null;

    let elegida: EleccionFiltro | null = null;
    if (tipo === TypeStr.Choice || TIPOS_RELACION.includes(tipo)) {
        elegida = eleccion('casillas', preferido(PREFERENCIA_LISTA) ?? filtros.find(esHijoDelExacto));
    } else if (tipo === TypeStr.Boolean) {
        elegida = eleccion('sino', preferido([BooleanFilter]));
    } else if (TIPOS_TEXTO.includes(tipo)) {
        elegida = eleccion('texto', preferido(PREFERENCIA_TEXTO));
    } else if (TIPOS_NUMERO_CON_CASTER.includes(tipo)) {
        elegida = eleccion('texto', preferido(PREFERENCIA_NUMERO));
    } else if (TIPOS_NUMERO_SIN_CASTER.includes(tipo)) {
        elegida = eleccion('texto', preferido([ExactSearchFilter]));
    } else if (TIPOS_FECHA.includes(tipo)) {
        elegida = eleccion('fechas', filtros.find(f => DateFilters.includes(f)));
    }
    if (!elegida && tipo !== null && filtros.includes(ExistsFilter)) {
        elegida = {tipo: 'sino', filtro: ExistsFilter};
    }
    return elegida;
}

/** Deja en un texto solo el primer número (entero o, si se admiten, con decimales; la coma vale como punto) */
function limpiarNumero(conDecimales: boolean): (texto: string) => string {
    const patron = conDecimales ? /-?\d+(\.\d+)?/ : /-?\d+/;
    return (texto: string) => {
        const encontrado = (conDecimales ? texto.replace(/,/g, '.') : texto).match(patron);
        return encontrado ? encontrado[0] : '';
    };
}

/** La sección por defecto de una propiedad (sin los añadidos de las relaciones, que dependen del tipo final) */
function seccionBase(propiedad: InternalPropertyMap, {tipo, filtro}: EleccionFiltro, esBuscador: boolean): SeccionFiltro {
    const seccion: SeccionFiltro = {titulo: propiedad.name, tipo, propiedad: propiedad.modelKey, filtro};
    const tipoPropiedad = tipoDe(propiedad);
    if (filtro === ExistsFilter) {
        // El dato de ExistsFilter tiene que ser boolean: el string "no" sería verdad
        seccion.convertir = (valor: string) => valor === 'si';
    } else if (tipo === 'casillas' && tipoPropiedad === TypeStr.Choice) {
        const choice = propiedad.type.asChoice();
        // La etiqueta es el propio valor (el Choice del cliente no tiene etiquetas aparte)
        seccion.opciones = choice.choices.map(c => ({etiqueta: c, valor: c}));
    } else if (tipo === 'casillas' && esBuscador) {
        seccion.tipo = 'buscador';
    } else if (tipo === 'texto' && !TIPOS_TEXTO.includes(tipoPropiedad)) {
        seccion.limpiar = limpiarNumero(tipoPropiedad !== TypeStr.Integer);
    }
    return seccion;
}

/** Las opciones de varios modelos juntas, ordenadas (una Uri a una clase padre); con uno, tal cual */
function opcionesDe(modelos: Type<Resource>[], opciones: OpcionesFiltroService): Observable<OpcionFiltro[]> {
    if (modelos.length === 1) {
        return opciones.opciones(modelos[0]);
    }
    return forkJoin(modelos.map(m => opciones.opciones(m))).pipe(
        map(listas => ([] as OpcionFiltro[]).concat(...listas).sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es'))));
}

function sugerenciasDe(modelos: Type<Resource>[], opciones: OpcionesFiltroService, texto: string): Observable<OpcionFiltro[]> {
    if (modelos.length === 1) {
        return opciones.sugerencias(modelos[0], texto);
    }
    return forkJoin(modelos.map(m => opciones.sugerencias(m, texto).pipe(catchError(() => of([] as OpcionFiltro[]))))).pipe(
        map(listas => ([] as OpcionFiltro[]).concat(...listas).slice(0, MAX_SUGERENCIAS)));
}

/** La etiqueta de un IRI: con varios modelos, la del primero que lo encuentre; si ninguno, el IRI */
function etiquetaDe(modelos: Type<Resource>[], opciones: OpcionesFiltroService, iri: string): Observable<string> {
    if (modelos.length === 1) {
        return opciones.etiqueta(modelos[0], iri);
    }
    return concat(...modelos.map(m => opciones.etiqueta(m, iri).pipe(catchError(() => EMPTY))))
        .pipe(take(1), defaultIfEmpty(iri));
}

/**
 * Lo que necesita una relación según su tipo final (con los ajustes ya aplicados): convertir el IRI a Uri y, en
 * buscador, sugerencias y etiqueta; en casillas, nacer vacía con «Ver todos» y cargar las opciones después. Lo que
 * traigan los ajustes manda.
 */
function completarRelacion(seccion: SeccionFiltro, propiedad: InternalPropertyMap, cambios: Partial<SeccionFiltro>,
                           modelos: Type<Resource>[], opciones: OpcionesFiltroService): SeccionFiltro {
    const completa: SeccionFiltro = {convertir: (iri: string) => opciones.aUri(propiedad, iri), ...seccion};
    if (completa.tipo === 'buscador') {
        completa.sugerencias = cambios.sugerencias || (texto => sugerenciasDe(modelos, opciones, texto));
        completa.etiquetaDe = cambios.etiquetaDe || (iri => etiquetaDe(modelos, opciones, iri));
    } else if (completa.tipo === 'casillas' && cambios.opciones === undefined) {
        completa.opciones = [];
        completa.destacadas = cambios.destacadas ?? [];
        completa.cargarOpciones = cambios.cargarOpciones || (() => opcionesDe(modelos, opciones));
    }
    return completa;
}

/** Lo listado en `orden` primero y en ese orden; el resto detrás, como venía */
function ordenar(secciones: SeccionFiltro[], orden: string[]): SeccionFiltro[] {
    const listadas = orden
        .map(propiedad => secciones.find(s => s.propiedad === propiedad))
        .filter(s => !!s);
    return [...listadas, ...secciones.filter(s => !listadas.includes(s))];
}

/**
 * Las secciones del panel de filtros a partir de las propiedades del modelo de una tabla (en su orden: columnas y
 * después el resto), según `filtroDeSeccion`. Sin sección: las propiedades `fijas` (llegan fijadas por el enlace del
 * menú), las `fueraDelPanelDeFiltros` y las que su modelo deja `sinFiltroEnElPanel` (en `@BasicEntity`). Después se
 * aplican los ajustes de la tabla.
 * Las casillas de relación nacen con `opciones: []` y `cargarOpciones`: quien use las secciones tiene que
 * suscribirse a `conOpcionesCargadas` y sustituir el array cuando lleguen.
 */
export function generarSecciones(propiedades: InternalPropertyMap[], ajustes: AjustesPanel | null,
                                 fijas: InternalPropertyMap[], opciones: OpcionesFiltroService): SeccionFiltro[] {
    const clavesFijas = (fijas || []).map(f => f.modelKey);
    const ajustesSecciones = ajustes?.secciones || {};
    const generadas: SeccionFiltro[] = [];
    for (const propiedad of propiedades) {
        if (clavesFijas.includes(propiedad.modelKey) || propiedad.fueraDelPanelDeFiltros || propiedad.sinFiltroEnElPanel) {
            continue;
        }
        const elegida = filtroDeSeccion(propiedad);
        if (!elegida) {
            continue;
        }
        const {oculta, ...cambios} = ajustesSecciones[propiedad.modelKey] || {};
        if (oculta) {
            continue;
        }
        // Una relación con solo ExistsFilter es un Sí / No, no una relación
        const esRelacion = TIPOS_RELACION.includes(tipoDe(propiedad)) && elegida.tipo === 'casillas';
        const modelos = esRelacion ? opciones.modelosDe(propiedad) : [];
        if (esRelacion && modelos.length === 0) {
            // Una Uri a una clase sin modelos registrados: no hay de dónde sacar opciones
            continue;
        }
        const esBuscador = modelos.some(m => opciones.usaBuscador(m));
        let seccion: SeccionFiltro = {...seccionBase(propiedad, elegida, esBuscador), ...cambios};
        if (esRelacion) {
            seccion = completarRelacion(seccion, propiedad, cambios, modelos, opciones);
        }
        generadas.push(seccion);
    }
    return ordenar([...generadas, ...(ajustes?.extra || [])], ajustes?.orden || []);
}

/**
 * Carga las opciones de las secciones que tienen `cargarOpciones` y emite, cada vez que llegan las de una, un array
 * nuevo de secciones con ellas (y con las que ya habían llegado); las demás secciones van tal cual. Sin nada que
 * cargar termina sin emitir.
 */
export function conOpcionesCargadas(secciones: SeccionFiltro[]): Observable<SeccionFiltro[]> {
    const cargas = secciones
        .filter(s => !!s.cargarOpciones)
        .map(s => s.cargarOpciones().pipe(map(opcionesCargadas => ({propiedad: s.propiedad, opciones: opcionesCargadas}))));
    if (cargas.length === 0) {
        return EMPTY;
    }
    return merge(...cargas).pipe(
        scan((cargadas, {propiedad, opciones}) => ({...cargadas, [propiedad]: opciones}), {} as { [p: string]: OpcionFiltro[] }),
        map(cargadas => secciones.map(s => cargadas[s.propiedad] ? {...s, opciones: cargadas[s.propiedad]} : s))
    );
}
